import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, ICON_SIZE, INK, MOTION, RADIUS } from "../../../theme/tokens";
import { ActionMenu } from "../Popups/ActionMenu";
import SystemButton from "../SystemButton/SystemButton";

/** One choice in a selection action's menu ("Group A", "Not assigned"). */
export type SelectionActionMenuItem = {
	id: string;
	/** Already translated. */
	label: string;
	/** Optional leading mark — a colour dot, a glyph. */
	leading?: ReactNode;
	/** Starts a new group: a hairline above it. */
	groupStart?: boolean;
	onSelect: () => void;
};

type SelectionActionBase = {
	/** Stable id for the React key and the derived testid. */
	id: string;
	/** Already-translated button label. */
	label: string;
	leadingIcon?: ReactNode;
	/** Defaults to "secondary". Use "delete" for a destructive action. */
	buttonVariant?: "secondary" | "delete" | "borderless" | "primary";
	loading?: boolean;
	/** Disable independently of the selection being empty (which already disables every action). */
	disabled?: boolean;
	/** Why the action is unavailable. A disabled bulk action with no stated reason reads as a bug. */
	tooltip?: ReactNode;
};

/**
 * A plain button (`onClick`), or — opt-in — an action that asks one more question first ("Set holiday group"
 * → which group): the button opens a menu of `menuItems` and picking one runs it.
 */
export type SelectionAction = SelectionActionBase & ({ onClick: () => void; menuItems?: never } | { menuItems: SelectionActionMenuItem[]; onClick?: never });

type SelectionActionBarProps = {
	/** Already-translated count line, e.g. "3 selected". */
	countLabel: string;
	actions: SelectionAction[];
	/** Renders a trailing Cancel that clears the selection. */
	onCancel?: () => void;
	/** Number of selected rows — every action is disabled at 0. */
	selectedCount: number;
	/**
	 * Accessible name for the group, e.g. "Bulk actions for selected employees". Required, because an
	 * unnamed group announces as an anonymous container. Must be STABLE: a name that changes with the
	 * count is re-announced on every tick, and the running total belongs in the page's own live
	 * region, not in this group's name.
	 */
	ariaLabel: string;
	dataTestId?: string;
};

/**
 * Contextual bar for acting on a multi-row selection: the live count on the left, the actions on
 * the right.
 *
 * It exists so bulk actions are visible at the moment a selection exists, rather than living in an
 * overflow menu the user has to remember — which is also why it takes its own Cancel instead of
 * relying on a "cancel selection" item somewhere else.
 */
export const SelectionActionBar: FC<SelectionActionBarProps> = ({ countLabel, actions, onCancel, selectedCount, ariaLabel, dataTestId }) => {
	const { t } = useTranslation();
	const hasSelection = selectedCount > 0;

	return (
		<Box
			data-testid={dataTestId}
			// A named group, so AT reaching these buttons announces what they belong to instead of
			// reading three unrelated buttons that appeared out of nowhere. Deliberately `group` and
			// not `toolbar`: ARIA APG expects a toolbar to be a single tab stop with arrow-key roving
			// focus, and these buttons are each independently tabbable. `group` carries no such promise.
			role="group"
			aria-label={ariaLabel}
			sx={(theme) => ({
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				gap: 2,
				flexWrap: "wrap",
				py: 1,
				px: 2,
				borderRadius: `${RADIUS.CARD}px`,
				backgroundColor: alpha(theme.palette.primary.main, 0.08),
				border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
			})}
		>
			<Box sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY }} data-testid={dataTestId ? `${dataTestId}-count` : undefined}>
				{countLabel}
			</Box>
			<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
				{actions.map((action) => {
					if (action.menuItems === undefined) {
						return (
							<SystemButton
								key={action.id}
								data-testid={dataTestId ? `${dataTestId}-${action.id}` : undefined}
								buttonVariant={action.buttonVariant ?? "secondary"}
								disabled={!hasSelection || action.disabled}
								tooltip={action.tooltip}
								loading={action.loading}
								onClick={action.onClick}
								leadingIcon={action.leadingIcon}
								noFullWidthMobile
								sx={{ fontSize: FONT_SIZE.EMPHASIS }}
							>
								{action.label}
							</SystemButton>
						);
					}
					// An action that asks one more question first: its button opens the house action menu of choices.
					// DEFAULT: a bar without a test id still needs one for the menu it opens; the action id keeps it unique.
					const menuTestId = dataTestId ? `${dataTestId}-${action.id}` : `selection-action-${action.id}`;
					return (
						<ActionMenu
							key={action.id}
							triggerLabel={action.label}
							items={action.menuItems.map((item) => ({
								id: item.id,
								label: item.label,
								icon: item.leading,
								groupStart: item.groupStart,
								onSelect: item.onSelect,
								dataTestId: `${menuTestId}-${item.id}`,
							}))}
							renderTrigger={({ open, ...trigger }) => (
								<SystemButton
									{...trigger}
									buttonVariant={action.buttonVariant ?? "secondary"}
									disabled={!hasSelection || action.disabled === true}
									tooltip={action.tooltip}
									loading={action.loading}
									leadingIcon={action.leadingIcon}
									noFullWidthMobile
									sx={{ fontSize: FONT_SIZE.EMPHASIS }}
								>
									{action.label}
									<ArrowDropDownIcon
										aria-hidden
										sx={{
											ml: 0.25,
											mr: -0.75,
											fontSize: ICON_SIZE.CONTROL,
											transition: `transform ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
											transform: open ? "rotate(180deg)" : "none",
											"@media (prefers-reduced-motion: reduce)": { transition: "none" },
										}}
									/>
								</SystemButton>
							)}
							dataTestId={menuTestId}
						/>
					);
				})}
				{onCancel && (
					<SystemButton
						data-testid={dataTestId ? `${dataTestId}-cancel` : undefined}
						buttonVariant="borderless"
						onClick={onCancel}
						leadingIcon={<CloseIcon sx={{ fontSize: 18 }} />}
						noFullWidthMobile
						sx={{ fontSize: FONT_SIZE.EMPHASIS }}
					>
						{t("cancel")}
					</SystemButton>
				)}
			</Box>
		</Box>
	);
};
